// Quote Builder — SJB / DTN ABS panel picker.
//
// Panels are STANDALONE edged-all-round products (end panels, infill panels),
// not parts of a cabinet. So this picker is dimension-entry rather than
// browse: the retailer types a height and a width, and the server answers
// with the cheapest stocked panel that covers it at each of 18 / 25 / 36mm.
//
// WHY NOT A SIZE LIST. There are 494 stocked sizes per thickness, 1482 rows in
// all. A list that long is not browsable, and the sizes a retailer actually
// wants are mostly not IN it — the grid is a 100mm lattice, so a 720 x 570
// panel is bought as 800 x 600 and cut down. Asking for the size you want and
// being told what covers it is the only interaction that matches the product.
//
// WHY "CHEAPEST" AND NOT "NEAREST". DTN price by sheet yield, so a larger
// panel is sometimes cheaper: the two rules disagree on 126 of the 494 sizes,
// by GBP 7.24 on average and GBP 49.91 at worst. Nearest is the intuitive
// rule and it overcharges. The server returns the cheapest; where the
// smallest covering panel differs, it is shown as an explanation so the
// retailer is not left wondering why a bigger panel is being quoted.
//
// CUTTING IS NOT INCLUDED IN THE PANEL PRICE. Cut / edge / route are separate
// addable lines from the Cut & Drill list, never folded in (Steve,
// 2026-09-16): which processing a job needs is its own decision, and a
// bundled charge cannot be taken off a line without editing a price.
//
// HINGE DRILLING IS THE ONE EXCEPTION, and it is not really one: a panel used
// as a door is drilled, and the drilling is committed as its OWN line beside
// the panel, sharing a group id. That is exactly what the SJB cabinet picker
// already does with its frontals, so the quote reads the same either way and
// the charge can still be deleted on its own. It is priced from DTN's
// per-hole ABS rate (2026CIDDABS), not invented.
//
// REUSE, NOT REINVENTION. The drilling UI is door-drilling-block.ts — the
// same block the door picker and the SJB cabinet picker use, extracted on
// 2026-09-04 precisely so a third caller could not drift from them. Grain
// wording matches TKC's own catalogue, which spells it "Vertical Grain" /
// "Horizontal Grain" inside product names (door-picker.ts:164); a panel is
// cut from a sheet so here it is a CHOICE rather than a separate SKU, but a
// quote that mixes TKC doors and DTN panels should use one vocabulary.
//
// POSSIBLE LATER REUSE (Steve, 2026-09-16): TKC offer made-to-measure sizing
// on some ranges, which is the same "type a size, get a price" shape. Nothing
// is abstracted for that yet and nothing should be until it is real — but the
// size-entry, option-card and commit steps are kept separable rather than
// interleaved, so lifting them later is a refactor and not a rewrite.

import { repaintPreservingScroll } from "@tkos/shared-ui";
import { setConfiguratorInProgress } from "./picker-selection";
import type { QuoteItem } from "./types";
import type { QuoteStore } from "./state";
import { escapeHtml, fmtGbp, newId } from "./formatters";
import { recalcItem } from "./pricing";
import {
  renderDrillingBlock, wireDrillingBlock, blankDrillingSpec,
  drillingPositions, holePositionText, drillAxis, type DrillingSpec,
} from "./door-drilling-block";
import {
  loadSjbPanelMeta, priceSjbPanel, sjbPanelToItem, loadSjbProcessing,
  loadSjbColours, getSjbDefaults, edgingKeyForLabel,
  SJB_SUPPLIER, SJB_EDGING_MATCHING,
  type SjbPanelMeta, type SjbPanelOption, type SjbPanelQuote,
  type SjbProcessingCharge, type SjbColour,
} from "./sjb-catalogue";
import { mountColourCombobox, type ColourComboHandle } from "./sjb-colour-combobox";

export interface SjbPanelPickerHandle { unmount(): void; }

/**
 * Grain direction.
 *
 * "" means NOT SPECIFIED, and that is the default on purpose. Grain is
 * meaningless on a plain colour and the majority of panels are plain, so
 * forcing a choice would put a decision on every line that most lines do not
 * have. It is not hard-gated like the blanking panel: there, a side is always
 * physically true and a default would fake a human choice; here, "no grain to
 * align" is itself a legitimate answer.
 *
 * The labels match TKC's catalogue wording so a quote carrying both a TKC
 * door and a DTN panel says the same thing twice rather than two things once.
 */
type Grain = "" | "vertical" | "horizontal";
const GRAIN_LABEL: Record<Exclude<Grain, "">, string> = {
  vertical: "Vertical Grain",
  horizontal: "Horizontal Grain",
};

/** DTN's per-hole drilling rate for an ABS door. An ABS panel used as a door
 *  is an ABS door, so this is the right one of DTN's two rates — the other,
 *  2026CIDDBID at twice the price, is for a door bought in from elsewhere. */
const ABS_DRILL_CODE = "2026CIDDABS";

/**
 * Debounce on the size inputs.
 *
 * 250ms was too eager: a four-digit size typed at an ordinary pace fired
 * mid-number, so the results area flickered through prices for 7mm, 72mm and
 * 720mm on the way to the one that was wanted (Steve, 2026-09-16). 400ms is
 * past the gap between digits and still well under the pause that reads as
 * lag. It is a backstop rather than the main path anyway — `change` fires on
 * blur and on Enter, and that prices immediately.
 */
const PRICE_DEBOUNCE_MS = 400;

/** What the board adjustment is called on a PANEL. The cabinet wording is
 *  "off carcass", which names a thing that is not on this line: a panel is
 *  board all the way through, so the percentage applies to the lot. */
const PANEL_TAG_NOUN = "list";

function boardAdjustText(c: SjbColour): string {
  if (c.kind === "uplift") return `+${Math.abs(c.discPct)}% board uplift`;
  if (c.kind === "discount") return `${c.discPct}% board discount`;
  return "an unreadable board group";
}

interface State {
  height: string;
  width: string;
  qty: number;
  zoneId: string;
  /** Which thickness card is selected. Null until the retailer picks one —
   *  never defaulted, because thickness is a real product choice and a
   *  pre-selected 18mm would be indistinguishable from a decision. */
  thickness: number | null;
  quote: SjbPanelQuote | null;
  grain: Grain;
  /** Reused wholesale from the door picker / SJB cabinet picker. mode "none"
   *  until the retailer says the panel is being used as a door. */
  drill: DrillingSpec;
  loading: boolean;
  /** Distinguishes "no answer yet" from "asked and nothing fits". */
  asked: boolean;
  error: string;
  /**
   * Board colour for THIS panel, and the tape that edges it.
   *
   * Seeded from the quote-level SJB defaults and then owned by the picker,
   * exactly as the cabinet picker's colour is: changing the quote default
   * afterwards must not reprice a panel already on the quote. Neither is
   * written back to the defaults — a panel is not the thing that should
   * re-point the next cabinet.
   */
  boardKey: string;
  boardLabel: string;
  edgingColour: string;
}

export function mountSjbPanelPicker(
  host: HTMLElement,
  store: QuoteStore,
  onDone: () => void,
): SjbPanelPickerHandle {
  let mounted = true;
  let meta: SjbPanelMeta | null = null;
  /** True only when the meta call FAILED. An absent `meta` otherwise means
   *  "still loading", and the two must not render the same — see the handing
   *  bug in sjb-cabinet-picker, where absent data claimed a positive fact. */
  let metaFailed = false;
  /** DTN's per-hole ABS rate. `null` until loaded; `undefined` never happens.
   *  A FAILED load is tracked separately so the screen can say the charge is
   *  unknown instead of quietly drilling for nothing. */
  let drillCharge: SjbProcessingCharge | null = null;
  let drillChargeFailed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let seq = 0;
  /** The 614 board decors, shared with the cabinet picker through the same
   *  cached loader. Empty until they arrive; `coloursFailed` is the separate
   *  fact, because "still loading" and "could not load" must not render the
   *  same — an absent colour list silently means full list. */
  let colours: SjbColour[] = [];
  let coloursFailed = false;
  let combo: ColourComboHandle | null = null;
  let edgingCombo: ColourComboHandle | null = null;
  /** The shell is written once; see the render section. */
  let shellUp = false;
  /** Zone list signature, so the one control that IS a <select> is rebuilt
   *  only when the zones actually change. */
  let zoneSig = "";

  const defaults = getSjbDefaults(store);
  const st: State = {
    height: "",
    width: "",
    qty: 1,
    zoneId: store.get().zones[0]?.id ?? "",
    thickness: null,
    quote: null,
    grain: "",
    drill: blankDrillingSpec(),
    loading: false,
    asked: false,
    error: "",
    boardKey: defaults.boardKey,
    boardLabel: defaults.boardLabel,
    edgingColour: defaults.edgingColour,
  };

  const dims = (): { h: number; w: number } => ({
    h: Math.round(Number(st.height) || 0),
    w: Math.round(Number(st.width) || 0),
  });
  const ready = (): boolean => { const d = dims(); return d.h > 0 && d.w > 0; };

  /** The option the retailer has selected, if it is actually orderable. */
  const chosen = (): SjbPanelOption | null => {
    if (!st.quote || st.thickness == null) return null;
    const o = st.quote.options.find((x) => x.thickness === st.thickness);
    return o && !o.unavailable ? o : null;
  };

  /**
   * Hole positions are measured against the panel the retailer ASKED for, not
   * the oversize stock panel it is cut from — the finished door is what gets
   * drilled.
   *
   * BOTH dimensions go in. A side-hung door's bores run down the height; a
   * top-hung flap's run across the width, and handing the helper only a
   * height would make it return nothing rather than measure the wrong axis.
   */
  const holes = (): number[] => drillingPositions(st.drill, { height: dims().h, width: dims().w });
  const drilledHoles = (): number => (st.drill.mode === "hinge" ? holes().length : 0);
  const drillTotal = (): number =>
    (drillCharge ? drilledHoles() * drillCharge.price * (st.qty || 1) : 0);

  function schedulePrice(): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; void fetchPrice(); }, PRICE_DEBOUNCE_MS);
  }

  /** Price NOW: the retailer has finished the number (blur / Enter) or has
   *  changed the colour, and there is nothing left to wait for. */
  function flushPrice(): void {
    if (timer) { clearTimeout(timer); timer = null; }
    void fetchPrice();
  }

  async function fetchPrice(): Promise<void> {
    if (!ready()) { st.quote = null; st.asked = false; paint(); return; }
    const d = dims();
    const mine = ++seq;
    st.loading = true;
    st.error = "";
    paint();
    try {
      const q = await priceSjbPanel(d.h, d.w, st.boardKey);
      // A slower earlier request must never overwrite a newer answer.
      if (!mounted || mine !== seq) return;
      st.quote = q;
      st.asked = true;
      // Keep the chosen thickness only if it is still orderable at the new
      // size; silently keeping a now-unavailable one would leave Add enabled
      // against nothing.
      if (st.thickness != null) {
        const still = q.options.find((o) => o.thickness === st.thickness && !o.unavailable);
        if (!still) st.thickness = null;
      }
    } catch (err) {
      if (!mounted || mine !== seq) return;
      st.quote = null;
      st.asked = false;
      // A rejected COLOUR is a different failure from a rejected size, and
      // saying "check the connection" about it sends the retailer to look in
      // the wrong place. The server refuses rather than quietly quoting list,
      // so the message has to name the real cause.
      const msg = err instanceof Error ? err.message : "";
      st.error = /unknown_colour|unreadable_board_group/.test(msg)
        ? "That board colour could not be priced. Choose the colour again — the catalogue may have changed."
        : "Could not price that size. Check the connection and try again.";
    } finally {
      if (mounted && mine === seq) { st.loading = false; paint(); }
    }
  }

  // ── render ──────────────────────────────────────────────────────────────
  //
  // THE SHELL IS WRITTEN ONCE AND THE DYNAMIC REGIONS ARE PATCHED INSIDE IT.
  //
  // Steve, 2026-09-16: "typing into height/width refreshes on every keystroke,
  // making dimensions hard to enter." It did. Every repaint replaced the whole
  // host, and repaintPreservingScroll — which does restore focus — cannot
  // restore the CARET on an `<input type=number>`: `selectionStart` is null
  // there, so its setSelectionRange guard never fires and the caret lands at
  // 0. The next digit then goes in front of the ones already typed. Quantity
  // was worse again: it repainted on every character AND rewrote the field
  // from clamped state, so clearing it to type "12" snapped back to "1".
  //
  // The fix is the one repaint-preserving-scroll.ts already names as the
  // deeper one, and that the colour combobox already does: render only what
  // changed. The size inputs, the quantity input, the zone select and the two
  // comboboxes are created ONCE and never replaced, so nothing can take the
  // caret off them — and the price still recalculates on blur, on Enter, and
  // 400ms after typing stops.
  //
  // repaintPreservingScroll still wraps the patch: the regions that ARE
  // replaced include the grain toggles and the drilling block, and a click
  // there must not throw the pane back to the top or lose the button.

  const region = (role: string): HTMLElement | null =>
    host.querySelector<HTMLElement>(`[data-role='${role}']`);

  function fill(role: string, html: string): void {
    const el = region(role);
    if (el) el.innerHTML = html;
  }

  function optionCard(o: SjbPanelOption): string {
    const sel = st.thickness === o.thickness;
    if (o.unavailable) {
      return `<button type="button" class="sjb-panel__card sjb-panel__card--out" disabled
                data-role="thickness-${o.thickness}">
          <span class="sjb-panel__thk">${o.thickness}mm</span>
          <span class="sjb-panel__none">No panel this large</span>
        </button>`;
    }
    const trimmed = (o.trimHeight || 0) > 0 || (o.trimWidth || 0) > 0;
    // The board adjustment is shown ON the card, not only in the totals: the
    // three cards are what the retailer compares, and a price that has moved
    // 45% without saying so is the kind of number that gets queried later.
    const adjusted = !!o.discPct && o.listPrice != null && o.listPrice !== o.price;
    return `<button type="button"
        class="sjb-panel__card${sel ? " is-selected" : ""}"
        aria-pressed="${sel ? "true" : "false"}"
        data-role="thickness-${o.thickness}" data-thickness="${o.thickness}">
      <span class="sjb-panel__thk">${o.thickness}mm</span>
      <span class="sjb-panel__price">${fmtGbp(o.price || 0)}</span>
      ${adjusted
        ? `<span class="sjb-panel__list" data-role="card-list-${o.thickness}">${
            fmtGbp(o.listPrice || 0)} list · ${escapeHtml(o.discountLabel || "")}</span>`
        : ""}
      <span class="sjb-panel__stock">${o.height} × ${o.width}mm</span>
      <span class="sjb-panel__code">${escapeHtml(o.code || "")}</span>
      ${o.exact
        ? `<span class="sjb-panel__tag sjb-panel__tag--exact">Stock size</span>`
        : trimmed
          ? `<span class="sjb-panel__tag">Cut down ${o.trimHeight}mm × ${o.trimWidth}mm</span>`
          : ""}
      ${o.rotated ? `<span class="sjb-panel__tag sjb-panel__tag--warn">Turned on its side</span>` : ""}
    </button>`;
  }

  /**
   * Why the quoted panel is not the smallest one that fits.
   *
   * Shown for the SELECTED thickness, or — before anything is selected — for
   * the first thickness that has one. Gating it on a selection meant it only
   * appeared after the retailer had already chosen, which is after the point
   * at which it was any use. All three thicknesses share one size grid, so
   * the first is representative; the thickness is named so it cannot be read
   * as applying to a card it does not describe.
   */
  function explainFor(): SjbPanelOption | null {
    const sel = chosen();
    if (sel?.nearest) return sel;
    if (sel) return null;          // a deliberate pick that needs no explaining
    return st.quote?.options.find((o) => !o.unavailable && o.nearest) ?? null;
  }

  function cheaperThanNearest(): string {
    const o = explainFor();
    if (!o || !o.nearest) return "";
    // Both figures are net of the board adjustment, as the server states
    // them — quoting a list-price gap beside two discounted prices would
    // overstate what the bigger panel is worth.
    return `<p class="sjb-panel__explain" data-role="cheaper-note">
      At <strong>${o.thickness}mm</strong>, the smallest panel that fits is
      <strong>${escapeHtml(o.nearest.code)}</strong> at ${o.nearest.height} × ${o.nearest.width}mm,
      but it costs ${fmtGbp(o.nearest.price)} — ${fmtGbp(o.nearest.saving)} more than the
      larger ${escapeHtml(o.code || "")}. DTN price by sheet yield, so a bigger panel is
      sometimes cheaper. The cheaper one is quoted.
    </p>`;
  }

  /** Any orderable option that only fits turned round. Not gated on the
   *  selection, for the same reason as above — it is a warning about the
   *  request, not about a card. */
  const anyRotated = (): boolean =>
    !!st.quote?.options.some((o) => !o.unavailable && o.rotated);

  /** Same primitive as the SJB cabinet picker's, so the two configurators
   *  render an identical control rather than two that merely look alike. */
  function toggleRow(role: string, value: string, opts: Array<[string, string]>): string {
    return `<div class="qb-w8-toggle" role="group" data-role="${escapeHtml(role)}">${
      opts.map(([v, l]) =>
        `<button type="button" class="${v === value ? "active" : ""}"
                 data-val="${escapeHtml(v)}" aria-pressed="${v === value}">${escapeHtml(l)}</button>`).join("")
    }</div>`;
  }

  /** The chosen colour as the server described it. Read from the colour list
   *  rather than from the price response, so the note is right before a size
   *  has been typed and the screen has anything to price. */
  const chosenColour = (): SjbColour | null =>
    colours.find((c) => c.key === st.boardKey) ?? null;

  // ── the shell: written once, never replaced ─────────────────────────────

  function shellHtml(): string {
    return `
      <div class="sjb-picker sjb-panel" data-role="sjb-panel-picker">
        <header class="sjb-picker__head">
          <div>
            <h3 class="sjb-picker__title"><span class="sup">SJB</span>Panel by size</h3>
            <p class="sjb-picker__sub">Edged all round · 18 / 25 / 36mm</p>
          </div>
          <button type="button" class="sjb-picker__close" data-role="cancel" aria-label="Cancel">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                 stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </header>

        <div data-role="panel-alerts"></div>
        <div data-role="panel-summary"></div>

        <!-- Colour first, because it is the only control on this screen that
             moves every price below it. -->
        <section class="sjb-picker__sec">
          <div class="qb-w8-row two">
            <div><div data-role="colour-slot"></div></div>
            <div><div data-role="edging-slot"></div></div>
          </div>
          <div data-role="finish-note"></div>
        </section>

        <div class="qb-w8-row two">
          <div>
            <label class="qb-w8-lbl" for="sjb-panel-h">Height (mm)</label>
            <input class="qb-w8-inp" id="sjb-panel-h" data-role="panel-height"
                   type="number" min="1" step="1" inputmode="numeric"
                   value="${escapeHtml(st.height)}" placeholder="e.g. 720">
          </div>
          <div>
            <label class="qb-w8-lbl" for="sjb-panel-w">Width (mm)</label>
            <input class="qb-w8-inp" id="sjb-panel-w" data-role="panel-width"
                   type="number" min="1" step="1" inputmode="numeric"
                   value="${escapeHtml(st.width)}" placeholder="e.g. 570">
          </div>
        </div>

        <div class="qb-w8-row two">
          <div>
            <label class="qb-w8-lbl" for="sjb-panel-zone">Zone</label>
            <select class="qb-w8-inp" id="sjb-panel-zone" data-role="panel-zone"></select>
          </div>
          <div>
            <label class="qb-w8-lbl" for="sjb-panel-qty">Quantity</label>
            <input class="qb-w8-inp" id="sjb-panel-qty" data-role="panel-qty"
                   type="number" min="1" step="1" value="${st.qty}">
          </div>
        </div>

        <div class="sjb-panel__results" data-role="panel-results"></div>
        <div data-role="panel-body"></div>
      </div>
    `;
  }

  // ── the dynamic regions ─────────────────────────────────────────────────

  function alertsHtml(): string {
    return `${st.error ? `<p class="sjb-picker__error" role="alert" data-role="error">${escapeHtml(st.error)}</p>` : ""}
      ${metaFailed ? `<p class="sjb-picker__warn" data-role="meta-failed">
          Panel size limits are unavailable, so a size that is too large will only be
          reported after you ask for it.
        </p>` : ""}`;
  }

  function summaryHtml(): string {
    const pick = chosen();
    const unit = pick?.price ?? 0;
    return `
      <div class="qb-w8-summary" data-role="unit-price">
        <div>
          <div class="lbl">Panel price${pick ? ` · ${escapeHtml(pick.code || "")}` : ""}</div>
          <div class="val">${pick ? fmtGbp(unit) : "—"}</div>
        </div>
        <div class="right">
          <span class="qb-w8-summary-mult">× ${st.qty}</span>
          <span class="qb-w8-summary-total">${pick ? fmtGbp(unit * (st.qty || 1)) : "—"}</span>
        </div>
      </div>`;
  }

  /**
   * What the chosen colour is doing to the price, and what the edging means.
   *
   * NO COLOUR IS A WARNING, NOT A BLANK. Until 2026-09-16 the panel picker
   * had no colour at all and every panel was quoted at DTN's full list, which
   * is the wrong price for every retailer with a board group. Leaving the
   * space empty would reproduce that silently, so the absence is stated.
   */
  function finishNoteHtml(): string {
    const parts: string[] = [];
    if (coloursFailed) {
      // Two different situations, and saying the wrong one is worse than
      // saying nothing. The price request still carries whatever colour the
      // quote defaults hold, so a failed LIST does not mean an undiscounted
      // PRICE — it means the colour cannot be changed from here.
      parts.push(st.boardKey
        ? `<p class="sjb-picker__warn" data-role="colour-failed">
             The board colour list could not be loaded, so the colour cannot be changed here.
             These panels are priced in the quote's colour,
             <strong>${escapeHtml(st.boardLabel || st.boardKey)}</strong>.
           </p>`
        : `<p class="sjb-picker__warn" data-role="colour-failed">
             The board colour list could not be loaded and no colour is set on this quote, so
             these panels are priced at DTN's <strong>full list</strong> with no board discount.
             Reload before quoting from this.
           </p>`);
    } else if (!st.boardKey) {
      parts.push(`<p class="sjb-picker__warn" data-role="no-colour">
        No board colour chosen — panels are priced at DTN's <strong>full list</strong>.
        Pick the colour to apply your board discount.
      </p>`);
    } else {
      const c = chosenColour();
      const adj = c ? boardAdjustText(c) : "";
      parts.push(`<p class="sjb-picker__hint" data-role="board-note">
        Priced in <strong>${escapeHtml(st.boardLabel || st.boardKey)}</strong>${
          adj ? ` · ${escapeHtml(adj)}` : ""}. The whole panel is board, so the
        adjustment applies to all of it.
      </p>`);
      if (c?.kind === "uplift") {
        // Flagged, not applied. DTN also list a flat "panel up premium" for
        // end panels from higher price groups (2026CIPUPSML / 2026CIPUPLGE);
        // whether a premium decor takes the percentage, the flat charge or
        // both is a question for SJB, so the percentage is applied — the same
        // as on a carcass — and the flat charges stay addable by hand.
        parts.push(`<p class="sjb-picker__note" data-role="uplift-note">
          Premium decor: the panel carries the same percentage uplift a carcass does.
          DTN also list a flat <strong>panel up premium</strong> for end panels from higher
          price groups — it is <strong>not</strong> added here. Add it from the
          Cut &amp; Drill list if this job needs it.
        </p>`);
      }
    }
    parts.push(`<p class="sjb-picker__hint" data-role="edging-note">
      ${st.edgingColour && st.edgingColour !== SJB_EDGING_MATCHING
        ? `Edged in <strong>${escapeHtml(st.edgingColour)}</strong> — carried on the line for
           the fabricator. Edge tape costs the same whatever the decor, so it does not
           move the price.`
        : `Edged all round to match the board. Pick a different decor for a contrast
           edge; either way the edging is already in the panel price.`}
    </p>`);
    return parts.join("");
  }

  function resultsHtml(): string {
    const d = dims();
    // Too big for the grid in ANY orientation — worth saying before the
    // retailer tries three thicknesses and gets the same answer each time.
    const overMax = meta && meta.maxAnyDimension > 0
      && Math.max(d.h, d.w) > meta.maxAnyDimension;

    if (!ready()) {
      return `<p class="sjb-picker__hint" data-role="prompt">Enter a height and a width to see prices.</p>`;
    }
    if (overMax) {
      return `<p class="sjb-picker__warn" data-role="too-big">
        The largest panel stocked is ${meta!.maxAnyDimension}mm on its longest side,
        so ${d.h} × ${d.w}mm cannot be supplied as one piece.
      </p>`;
    }
    if (st.loading) {
      return `<p class="sjb-picker__hint" data-role="loading">Pricing ${d.h} × ${d.w}mm…</p>`;
    }
    if (!st.asked || !st.quote) return "";
    if (!st.quote.ok) {
      return `<p class="sjb-picker__warn" data-role="no-fit">
        No stocked panel covers ${d.h} × ${d.w}mm at any thickness.
      </p>`;
    }
    return `<p class="qb-w8-lbl">Cheapest panel covering ${d.h} × ${d.w}mm</p>
      <div class="sjb-panel__cards">${st.quote.options.map(optionCard).join("")}</div>
      ${cheaperThanNearest()}
      ${anyRotated()
        ? `<p class="sjb-picker__warn" data-role="rotated-note">
             This only fits with the panel turned on its side. On a woodgrain
             decor the grain runs the length of the panel, so check that is
             acceptable before ordering.
           </p>`
        : ""}`;
  }

  function bodyHtml(): string {
    const pick = chosen();
    return `
      <div class="qb-w8-row" data-role="grain-row">
        <div>
          <label class="qb-w8-lbl">Grain direction</label>
          ${toggleRow("grain", st.grain, [
            ["", "Not specified"], ["vertical", "Vertical"], ["horizontal", "Horizontal"],
          ])}
          <p class="sjb-picker__hint">
            ${st.grain
              ? `Ordered as ${escapeHtml(GRAIN_LABEL[st.grain as Exclude<Grain, "">])}${
                  anyRotated()
                    ? " — measured on the finished panel. This one is cut from a panel turned on its side, so the grain runs across the stock sheet."
                    : ""}`
              : "Leave unspecified on a plain colour. On a woodgrain, set it so adjacent doors and panels align."}
          </p>
        </div>
      </div>

      <div data-role="panel-drill-slot">
        ${renderDrillingBlock(st.drill, {
          id: "sjb-panel",
          title: "Drilling & Handing — if this panel is a door",
          note: st.drill.mode === "hinge"
            ? (drillChargeFailed
                ? "Could not load the drilling charge, so the holes below are not costed."
                : drillCharge
                  ? `${fmtGbp(drillCharge.price)} per hole (${escapeHtml(drillCharge.code)})`
                  : "Loading the drilling charge…")
            : "A panel used as an end panel needs no drilling. Switch to 35mm Hinge if it is a door.",
        })}
      </div>

      ${st.drill.mode === "hinge" && ready()
        ? `<p class="sjb-picker__hint" data-role="drill-summary">
             ${escapeHtml(holePositionText(st.drill))} ${
               drillAxis(st.drill) === "horizontal"
                 ? `across a ${dims().w}mm width`
                 : `on a ${dims().h}mm panel`}${
               drillCharge ? ` · ${fmtGbp(drillTotal())} for ${drilledHoles() * (st.qty || 1)} hole${
                 drilledHoles() * (st.qty || 1) === 1 ? "" : "s"}` : ""}
           </p>`
        : ""}

      <p class="sjb-picker__hint" data-role="processing-note">
        Cutting and edging are <strong>not</strong> included in this price.
        Add them from the <strong>Cut &amp; Drill</strong> list as separate lines.
        ${st.drill.mode === "hinge"
          ? "Hinge drilling is the exception — it is added as its own line beside the panel."
          : ""}
      </p>

      <div class="sjb-picker__foot">
        <button type="button" class="qb-btn" data-role="cancel-2">Cancel</button>
        <button type="button" class="qb-btn qb-btn--primary" data-role="add"
                ${pick ? "" : "disabled"}>
          ${pick ? `Add ${pick.thickness}mm panel` : "Add panel"}
        </button>
      </div>`;
  }

  /** The zone <select> is rebuilt only when the zone LIST changes. Rewriting
   *  it on every paint would be harmless today, but it is the one remaining
   *  control that holds a selection, and this keeps the rule uniform: nothing
   *  the retailer is interacting with gets replaced under them. */
  function syncZones(): void {
    const zones = store.get().zones;
    const sig = zones.map((z) => `${z.id}::${z.name}`).join("|");
    if (sig === zoneSig) return;
    zoneSig = sig;
    const sel = region("panel-zone") as HTMLSelectElement | null;
    if (!sel) return;
    if (!zones.some((z) => z.id === st.zoneId)) st.zoneId = zones[0]?.id ?? "";
    sel.innerHTML = zones.map((z) =>
      `<option value="${escapeHtml(z.id)}"${z.id === st.zoneId ? " selected" : ""}>${escapeHtml(z.name)}</option>`).join("");
    sel.value = st.zoneId;
  }

  function paint(): void {
    if (!mounted) return;
    if (!shellUp) { mountShell(); return; }
    repaintPreservingScroll(host, paintNow);
  }

  function mountShell(): void {
    host.innerHTML = shellHtml();
    shellUp = true;
    wireShell();
    mountCombos();
    paintNow();
  }

  function paintNow(): void {
    if (!mounted || !shellUp) return;
    syncZones();
    fill("panel-alerts", alertsHtml());
    fill("panel-summary", summaryHtml());
    fill("finish-note", finishNoteHtml());
    fill("panel-results", resultsHtml());
    fill("panel-body", bodyHtml());
    wireDynamic();
  }

  /**
   * The two comboboxes.
   *
   * Mounted once the 614 decors have arrived, and then left alone: a repaint
   * must not destroy a combobox whose filter box is being typed into — that
   * is the same bug as the size inputs, and the combobox already carries its
   * own note about it.
   */
  function mountCombos(): void {
    if (!mounted || !colours.length) return;
    const slot = region("colour-slot");
    if (slot && !combo) {
      combo = mountColourCombobox(slot, {
        colours,
        selectedKey: st.boardKey,
        label: "Board colour",
        role: "sjb-panel-colour",
        // "list" and not "carcass": a panel has no carcass, and the whole
        // line is board, so the percentage really does apply to all of it.
        tagNoun: PANEL_TAG_NOUN,
        placeholder: "Choose a board colour",
        onPick: (c) => {
          st.boardKey = c.key;
          st.boardLabel = c.label;
          // A colour change moves every price on screen, so it is priced at
          // once rather than waiting out the size debounce.
          paint();
          flushPrice();
        },
      });
    }
    const eSlot = region("edging-slot");
    if (eSlot && !edgingCombo) {
      edgingCombo = mountColourCombobox(eSlot, {
        colours,
        selectedKey: edgingKeyForLabel(st.edgingColour, colours),
        label: "Edging",
        role: "sjb-panel-edging",
        // Flat, exactly as on a cabinet: edge tape is a flat cost whatever
        // the decor, so a "% off" tag beside it would be a claim about a
        // number that does not apply.
        flat: true,
        placeholder: SJB_EDGING_MATCHING,
        pinned: { key: SJB_EDGING_MATCHING, label: SJB_EDGING_MATCHING },
        onPick: (c) => {
          st.edgingColour = c.key === SJB_EDGING_MATCHING ? SJB_EDGING_MATCHING : c.label;
          // Not priced, so nothing is refetched — only the wording changes.
          paint();
        },
      });
    }
  }

  // ── wiring ──────────────────────────────────────────────────────────────

  const q = <T extends HTMLElement>(role: string): T | null =>
    host.querySelector<T>(`[data-role='${role}']`);

  /** Bound ONCE, to controls that are never replaced. */
  function wireShell(): void {
    const hIn = q<HTMLInputElement>("panel-height");
    const wIn = q<HTMLInputElement>("panel-width");
    // `input` keeps state in step with the field but never writes back to it;
    // `change` (blur, or Enter) means the number is finished, so it prices
    // immediately instead of waiting out the debounce.
    hIn?.addEventListener("input", (e) => {
      st.height = (e.target as HTMLInputElement).value;
      schedulePrice();
    });
    hIn?.addEventListener("change", () => { flushPrice(); });
    wIn?.addEventListener("input", (e) => {
      st.width = (e.target as HTMLInputElement).value;
      schedulePrice();
    });
    wIn?.addEventListener("change", () => { flushPrice(); });

    const qtyIn = q<HTMLInputElement>("panel-qty");
    qtyIn?.addEventListener("input", (e) => {
      // Read, clamped for arithmetic, and NOT written back. Writing the
      // clamped value into the field is what made an emptied box snap to "1"
      // before the retailer could finish typing "12".
      const n = Math.round(Number((e.target as HTMLInputElement).value) || 0);
      st.qty = n > 0 ? n : 1;
      paint();
    });
    qtyIn?.addEventListener("change", (e) => {
      // Normalise once, when the retailer has left the field: an empty or
      // zero box should not stay looking like a quantity of nothing.
      const el = e.target as HTMLInputElement;
      if (el.value !== String(st.qty)) el.value = String(st.qty);
    });

    q<HTMLSelectElement>("panel-zone")?.addEventListener("change", (e) => {
      st.zoneId = (e.target as HTMLSelectElement).value;
    });

    q<HTMLButtonElement>("cancel")?.addEventListener("click", () => onDone());
  }

  /** Rebound after every patch, to the markup that patch just wrote. */
  function wireDynamic(): void {
    for (const t of [18, 25, 36]) {
      q<HTMLButtonElement>(`thickness-${t}`)?.addEventListener("click", () => {
        st.thickness = st.thickness === t ? null : t;
        paint();
      });
    }

    host.querySelectorAll<HTMLButtonElement>("[data-role='grain'] button").forEach((b) => {
      b.addEventListener("click", () => {
        st.grain = (b.dataset["val"] ?? "") as Grain;
        paint();
      });
    });

    // Scoped to the block element, not the picker: wireDrillingBlock binds by
    // data-role inside whatever it is handed, and querying picker-wide is the
    // instancing bug the block was refactored to avoid.
    const block = host.querySelector<HTMLElement>("[data-role='drill-block']");
    if (block) {
      // No `persist`: a hinge side belongs to this panel, not to the quote.
      // The door picker remembers its drilling defaults; that is its policy,
      // not the block's, and copying it here would let one panel re-point the
      // next one.
      wireDrillingBlock(block, st.drill, { onChange: () => paint() });
    }

    q<HTMLButtonElement>("cancel-2")?.addEventListener("click", () => onDone());
    q<HTMLButtonElement>("add")?.addEventListener("click", () => confirm());
  }

  // ── commit ──────────────────────────────────────────────────────────────

  function confirm(): void {
    const pick = chosen();
    // Defence in depth: the button is disabled without a selection, but a
    // disabled button is not a guarantee, and this commits a price.
    if (!pick || !st.quote) return;
    const zoneId = st.zoneId || store.get().zones[0]?.id || "";
    const markupPct = store.get().defaults.markupPct ?? 0;
    // Shared id so the panel and its drilling read as one thing on the quote
    // and can be found together, exactly as the SJB cabinet picker groups a
    // cabinet with its frontal and drilling.
    const groupId = newId("g");
    const grainLabel = st.grain ? GRAIN_LABEL[st.grain] : "";
    const lines: QuoteItem[] = [];

    const panel = sjbPanelToItem(pick, st.quote, {
      qty: st.qty, zoneId, markupPct,
      // The colour is what makes the price the price, so it is snapshotted
      // onto the line with it. Edging is a specification rather than a
      // charge, and rides along for the fabricator.
      boardKey: st.boardKey, boardLabel: st.boardLabel, edgingColour: st.edgingColour,
    });
    panel.sjbGroupId = groupId;
    if (grainLabel) {
      panel.desc = `${panel.desc} · ${grainLabel}`;
      // Stated against the FINISHED panel. On a rotated panel that is not the
      // same as the grain of the stock sheet, and the supplier needs to know
      // which one is meant or they will cut it the wrong way round.
      panel.notes = `${panel.notes} · ${grainLabel}${
        pick.rotated ? " (on the finished panel; stock panel is turned)" : ""}`;
    }
    if (st.drill.mode === "hinge") {
      panel.hinging = st.drill.side === "R" ? "R" : st.drill.side === "T" ? "N" : "L";
      panel.notes = `${panel.notes} · ${holePositionText(st.drill)}`;
    }
    lines.push(panel);

    // Drilling as its own line. Only when there is a rate to price it with:
    // committing holes at GBP 0.00 because a lookup failed is the silent
    // undercharge this codebase keeps finding, so it is refused instead.
    if (st.drill.mode === "hinge" && drillCharge && drilledHoles() > 0) {
      const n = drilledHoles() * (st.qty || 1);
      const drilling: QuoteItem = {
        id: newId("i"),
        category: "sundries",
        supplier: SJB_SUPPLIER,
        code: drillCharge.code,
        desc: `Hinge drilling — ${n} hole${n === 1 ? "" : "s"} (${pick.thickness}mm panel)`,
        qty: 1,
        width: 0, height: 0, depth: 0,
        notes: [
          `For the ${st.quote.requested.height} x ${st.quote.requested.width}mm panel`,
          holePositionText(st.drill),
          `${st.drill.side === "R" ? "Right" : st.drill.side === "T" ? "Top" : "Left"} hand, ${st.drill.unitType} unit`,
          `${fmtGbp(drillCharge.price)} per hole x ${n}`,
        ].join(" | "),
        listPrice: +(drillCharge.price * n).toFixed(2),
        discPct: 0,
        tradePrice: 0, markupMode: "pct", markupPct, markupGbp: 0, retailPrice: 0,
        sjbGroupId: groupId,
      };
      recalcItem(drilling, "listPrice");
      lines.push(drilling);
    }

    store.set((draft) => {
      const zone = draft.zones.find((z) => z.id === zoneId) ?? draft.zones[0];
      if (zone) zone.items.push(...lines);
    });
    onDone();
  }

  // ── boot ────────────────────────────────────────────────────────────────

  // Typed sizes, a thickness, a grain direction and a drilling spec — all of
  // it lost if the pane is replaced. See setConfiguratorInProgress.
  setConfiguratorInProgress("the panel you are sizing");

  paint();
  void (async () => {
    try {
      const m = await loadSjbPanelMeta();
      if (!mounted) return;
      meta = m;
    } catch {
      if (!mounted) return;
      metaFailed = true;
    }
    try {
      colours = await loadSjbColours();
      if (!mounted) return;
      // The slots exist from the first paint, so the combos drop straight in
      // as soon as there is something to put in them.
      mountCombos();
    } catch {
      if (!mounted) return;
      coloursFailed = true;
    }
    try {
      const charges = await loadSjbProcessing();
      if (!mounted) return;
      drillCharge = charges.find((c) => c.code === ABS_DRILL_CODE) ?? null;
      // Present-but-absent is not the same as failed: if DTN stop supplying
      // the row, say the charge is unavailable rather than showing a spinner
      // for ever.
      if (!drillCharge) drillChargeFailed = true;
    } catch {
      if (!mounted) return;
      drillChargeFailed = true;
    }
    paint();
  })();

  return {
    unmount(): void {
      mounted = false;
      setConfiguratorInProgress(null);
      if (timer) { clearTimeout(timer); timer = null; }
      // Both comboboxes bind a document-level click listener to close their
      // popover; dropping the markup without unmounting would leave those
      // behind pointing at detached nodes.
      combo?.unmount(); combo = null;
      edgingCombo?.unmount(); edgingCombo = null;
      shellUp = false;
      host.innerHTML = "";
    },
  };
}
